/**
 * @lodex-pro/web-shared/auth/cookie-storage-adapter — Amplify v6 KeyValueStorageInterface
 * backed by a server-side httpOnly cookie (for the refresh token) + an in-memory Map
 * (for the access token + id token + everything else).
 *
 * Phase 4 / Plan 04-09 — implements CONTEXT D-07 (web token storage strategy) verbatim from
 * RESEARCH § Pattern 5 lines 384-440. The browser cannot set httpOnly cookies from JS — only
 * the server can via the Set-Cookie response header — so the adapter rewrites Amplify's
 * `setItem(<key>.refreshToken, value)` call into a `POST /v1/auth/cookie-exchange` round-trip
 * that lets the API issue the cookie (Phase 4 / Plan 04-05 implements the endpoint pair).
 *
 * Phase 4 / Plan 04-11 (CR-06 + WR-02) — TWO additions:
 *   1. `primeMemoryEntry(key, value)` — public typed escape hatch for the
 *      api-client's 401-refresh-retry path to write a fresh access token
 *      directly into the in-memory Map under the canonical Amplify v6 storage
 *      key (CognitoIdentityServiceProvider.<clientId>.<sub>.accessToken),
 *      bypassing the cookie-exchange routing in `setItem` (which is correct
 *      for `.refreshToken` keys but wrong for an access-token write).
 *   2. `setItem` / `removeItem` / `clear()` now THROW on non-ok responses from
 *      the cookie-exchange endpoint. Previously they discarded the response,
 *      so a 4xx/5xx silently left Amplify believing the call succeeded and
 *      the cookie remained unset/stale. Throwing surfaces the failure to
 *      Amplify's caller (which routes to the consumer's error mapper). WR-02
 *      closure.
 *
 * PITFALL 2 (RESEARCH lines 497-506) — MEDIUM CONFIDENCE PATTERN:
 *   Amplify v6's storage-key naming (`.refreshToken` suffix) is INTERNAL API; the storage
 *   adapter contract is documented but the per-key naming is not contract-stable across
 *   patch releases. Mitigations:
 *     1. `aws-amplify` is pinned to EXACT 6.17.0 (not `^6.17.0`) in package.json. ANY bump
 *        of that version requires re-running plan 04-10's HUMAN-UAT round-trip against
 *        real Cognito before the bump merges.
 *     2. The cookie-storage-adapter.test.ts unit suite asserts the `.refreshToken` suffix
 *        contract — a future Amplify patch that renames the suffix will surface as a unit
 *        test failure long before it ships.
 *     3. Plan 04-10 HUMAN-UAT signs off the real-Cognito round-trip on this exact Amplify
 *        version pin before Phase 4 closes.
 *
 * RESEARCH Sequence Diagram E (lines 1175-1217) shows the full round-trip:
 *   signIn() → Amplify writes refresh token to storage → setItem intercepts → POST cookie-exchange
 *   → server Set-Cookie: lodex_refresh; HttpOnly; Secure; SameSite=Strict;
 *   Path=/v1/auth/refresh; Max-Age=2592000.
 *
 * Spec: master spec §7 (web auth); §43 (cookie hardening checklist);
 *       CONTEXT D-07; RESEARCH § Pattern 5 + § Pitfall 2 + § Sequence Diagram E;
 *       Plan 04-11 — CR-06 + WR-02.
 */

import type { KeyValueStorageInterface } from 'aws-amplify/utils';

/**
 * Adapter constructor input. `apiBaseUrl` is the SAME origin the web app's API client
 * targets (e.g. `https://api.lodex.pro` in prod, `http://localhost:3000` in dev).
 * MUST be same-origin with the page so the browser will accept Set-Cookie on the POST
 * response and so subsequent /v1/auth/refresh POSTs (which are SameSite=Strict) succeed.
 */
export interface CookieExchangeStorageAdapterOpts {
  /** Full origin of the lodeX Pro API, e.g. `https://api.lodex.pro`. NO trailing slash. */
  readonly apiBaseUrl: string;
}

/**
 * Amplify v6 `KeyValueStorageInterface` implementation that splits storage:
 *
 *   - Keys ending in `.refreshToken` → POST/DELETE `/v1/auth/cookie-exchange`. The server
 *     sets/clears the `lodex_refresh` httpOnly cookie. JS NEVER sees the refresh token.
 *   - All other keys → in-memory Map, scoped to the tab. On tab close, both the access
 *     token and the Amplify session state are gone (the refresh cookie persists until
 *     Cognito-side rotation or sign-out).
 *
 * The interface methods are async per Amplify v6's contract — Amplify awaits them.
 *
 * Verbatim shape per RESEARCH § Pattern 5 lines 396-439 — DO NOT paraphrase. Any deviation
 * from this body must be coordinated with plan 04-10's HUMAN-UAT (the integration test
 * against real Cognito is the only way to know whether Amplify's interaction with the
 * adapter still works after a change).
 */
export class CookieExchangeStorageAdapter implements KeyValueStorageInterface {
  private readonly memory = new Map<string, string>();
  private readonly apiBaseUrl: string;

  public constructor(opts: CookieExchangeStorageAdapterOpts) {
    this.apiBaseUrl = opts.apiBaseUrl;
  }

  /**
   * Amplify writes to storage on every `signIn` / `confirmSignIn` / token refresh.
   * Keys ending in `.refreshToken` are routed to the server's cookie-exchange endpoint;
   * the server replies with `Set-Cookie: lodex_refresh=...; HttpOnly; Secure;
   * SameSite=Strict; Path=/v1/auth/refresh; Max-Age=2592000`. We store NOTHING locally
   * for the refresh token — the cookie is the single source of truth.
   *
   * For non-refresh keys (access token, id token, internal Amplify state) we use the
   * in-memory Map. These do not need to outlive the tab.
   *
   * Plan 04-11 (WR-02 closure): throws on a non-ok response from cookie-exchange so
   * Amplify surfaces the failure to the caller (sign-in flow / signOut flow / consumer
   * app's error mapper). Silently swallowing the failure was the prior bug — it left
   * Amplify believing the cookie was set when it was not.
   */
  public async setItem(key: string, value: string): Promise<void> {
    if (key.endsWith('.refreshToken')) {
      const res = await fetch(`${this.apiBaseUrl}/v1/auth/cookie-exchange`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: value }),
      });
      if (!res.ok) {
        throw new Error(`cookie-exchange POST failed: ${String(res.status)}`);
      }
      return;
    }
    this.memory.set(key, value);
  }

  /**
   * Amplify reads from storage when it needs to rehydrate session state. For refresh
   * tokens we return `null` — the cookie is HttpOnly, so JS cannot read it. Amplify
   * caches the in-memory access token; when it expires the `/v1/auth/refresh` POST
   * (cookie auto-sent) returns a fresh access token and the api-client primes Amplify
   * directly via `primeAccessTokenFromRefresh` (Plan 04-11 CR-06 fix — the
   * `fetchAuthSession({ forceRefresh: true })` round-trip that originally lived here
   * could not work because the refresh cookie is HttpOnly + Amplify cannot read it).
   *
   * For non-refresh keys we return the in-memory value (or null if absent).
   *
   * The method is `async` to satisfy Amplify v6's `KeyValueStorageInterface`
   * contract (Promise return type) even though the implementation is purely
   * synchronous in-memory access. The eslint-disable is intentional and
   * mechanical — required to honor the upstream interface signature.
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  public async getItem(key: string): Promise<string | null> {
    if (key.endsWith('.refreshToken')) {
      // The refresh token lives in the httpOnly cookie. JS cannot read it.
      return null;
    }
    return this.memory.get(key) ?? null;
  }

  /**
   * `signOut()` or token rotation may call removeItem on a refresh-token key. Tell the
   * server to clear the cookie via DELETE; the server responds with `Set-Cookie:
   * lodex_refresh=; Max-Age=0; SameSite=Strict; Path=/v1/auth/refresh`.
   *
   * Plan 04-11 (WR-02 closure): throws on a non-ok DELETE response.
   */
  public async removeItem(key: string): Promise<void> {
    if (key.endsWith('.refreshToken')) {
      const res = await fetch(`${this.apiBaseUrl}/v1/auth/cookie-exchange`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`cookie-exchange DELETE failed: ${String(res.status)}`);
      }
      return;
    }
    this.memory.delete(key);
  }

  /**
   * `clear()` wipes the in-memory Map AND issues a server-side cookie clear. This is
   * the path Amplify takes on a global signOut and on token-storage reset. We always
   * issue the DELETE — calling DELETE when no cookie is present is a no-op server-side
   * (the cookie attributes are still echoed back with Max-Age=0 which is harmless).
   *
   * Plan 04-11 (WR-02 closure): throws on a non-ok DELETE response. The in-memory
   * Map is still cleared before the throw so a partial clear-then-fail does not
   * leave stale access tokens behind for the next consumer code path.
   */
  public async clear(): Promise<void> {
    this.memory.clear();
    const res = await fetch(`${this.apiBaseUrl}/v1/auth/cookie-exchange`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(`cookie-exchange DELETE failed: ${String(res.status)}`);
    }
  }

  /**
   * Phase 4 / Plan 04-11 (CR-06 fix). Typed escape hatch for the api-client's
   * 401-refresh-retry path: write a new access token into the in-memory map
   * under the canonical Amplify v6 storage key
   * (CognitoIdentityServiceProvider.<clientId>.<sub>.accessToken) WITHOUT
   * going through `setItem` (which would route refresh-token keys to the
   * cookie-exchange endpoint — wrong for an access-token write).
   *
   * Web-shared-internal — NOT part of the KeyValueStorageInterface contract.
   * Only `amplify-bootstrap.ts.primeAccessTokenFromRefresh` should call this.
   */
  public primeMemoryEntry(key: string, value: string): void {
    this.memory.set(key, value);
  }

  /**
   * Synchronous read/delete for the same refresh-priming path: it needs the
   * `LastAuthUser` Amplify builds its keys from, and to drop the stale id token
   * under them. Web-shared-internal, like `primeMemoryEntry`.
   */
  public peekMemoryEntry(key: string): string | null {
    return this.memory.get(key) ?? null;
  }

  public dropMemoryEntry(key: string): void {
    this.memory.delete(key);
  }
}
